import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useRef, useState} from "react";

import SimpleMarkdown from "@khanacademy/simple-markdown";
import Button from "@khanacademy/wonder-blocks-button";
import {
    addStyle,
    useOnMountEffect,
    View,
} from "@khanacademy/wonder-blocks-core";
// eslint-disable-next-line no-restricted-imports -- TODO(FEI-7300): Replace with new semanticColor
import {color, semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {Markdown, type MarkdownRule} from "@khan/component-markdown";
import {GuideColors} from "@khan/guide-ui";

import {ellipsify} from "./utils.ts";

// We include this special one-off Markdown rule for marking Khanmigo's
// highlights. It matches inline text wrapped in "@@@@" delimeters. Before
// we let the Markdown rules run, we preprocess the selection to wrap all
// selected inlines in "@@@@" delimeters.
const highlightRule: MarkdownRule = {
    order: -1000,
    match: SimpleMarkdown.inlineRegex(/^\s*@@@@([\s\S]*)@@@@/),
    parse: (capture, parse, state) => ({
        type: "khanmigoHighlight",
        content: capture[1],
    }),
    react: (node, output, state) => {
        const renderedOutput =
            typeof node.content === "string"
                ? node.content
                : output(node.content);

        const children = renderedOutput || (
            <>
                <StyledSpan role="none" style={cursorStyles.cursorLine}>
                    <StyledSpan role="none" style={cursorStyles.cursorLabel} />
                </StyledSpan>
            </>
        );

        return <StyledMark key={state.key}>{children}</StyledMark>;
    },
};

const cursorRule: MarkdownRule = {
    order: -999,
    match: SimpleMarkdown.inlineRegex(/^\s*@@@!!!@@@/),
    parse: () => ({
        type: "khanmigoCursor",
        content: "",
    }),
    react: (node, output, state) => {
        return (
            <StyledMark key={state.key}>
                <StyledSpan role="none" style={cursorStyles.cursorLine}>
                    <StyledSpan role="none" style={cursorStyles.cursorLabel} />
                </StyledSpan>
            </StyledMark>
        );
    },
};

const splitAndWrapSelectionInHighlights = (selection: string) => {
    if (!selection) {
        return "@@@!!!@@@";
    }

    return selection
        .split("\n\n")
        .map((part) => "@@@@" + part + "@@@@")
        .join("\n\n");
};

const StaticMarkdownDocWithHighlight = (props: {text: string}) => {
    const ref = useRef<HTMLDivElement | null>(null);

    useOnMountEffect(() => {
        const element = ref.current;
        const mark = element?.querySelector("mark");
        if (element && mark) {
            // Scroll the container element to have the highlight mark
            // roughly halfway in the middle of the visible area:
            element.scrollTop = mark.offsetTop - element.offsetHeight / 2;
        }
    });

    return (
        <View ref={ref} style={styles.doc}>
            <Markdown
                source={props.text}
                overrideRules={{
                    khanmigoHighlight: highlightRule,
                    khanmigoCursor: cursorRule,
                }}
            />
        </View>
    );
};

export const CoeditorSnapshot = (props: {
    coeditorState: {prefix: string; selection: string; suffix: string};
    children?: React.ReactElement;
    alignment: "start" | "end";
}): React.ReactElement | null => {
    const {coeditorState, children} = props;
    const [expanded, setExpanded] = useState(false);

    const {prefix = "", selection = "", suffix = ""} = coeditorState;
    const highlightedSelection = splitAndWrapSelectionInHighlights(selection);
    const fullText = prefix + highlightedSelection + suffix;

    const selectionMessage = selection
        ? t`Selected: “${ellipsify(selection, 36, "end")}”`
        : t`Cursor after: “${ellipsify(prefix, 36, "start")}”`;

    const alignmentStyle =
        props.alignment === "start" ? styles.rowStart : styles.rowEnd;
    return (
        <>
            {children}
            <View style={[styles.row, alignmentStyle]}>
                <View style={styles.header}>
                    <View>
                        <LabelSmall style={styles.headerText}>
                            {t`Document at time of message`}
                        </LabelSmall>
                        <LabelSmall style={styles.headerText}>
                            {selectionMessage}
                        </LabelSmall>
                    </View>
                    <Button
                        onClick={() => setExpanded(!expanded)}
                        kind="secondary"
                        style={styles.button}
                        size="small"
                    >
                        {expanded ? t`Collapse document` : t`Show document`}
                    </Button>
                </View>
                {expanded && <StaticMarkdownDocWithHighlight text={fullText} />}
            </View>
        </>
    );
};

const StyledSpan = addStyle("span");

const styles = StyleSheet.create({
    row: {
        width: "80%",
        border: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        borderRadius: spacing.xSmall_8,
        marginInlineStart: spacing.small_12,
        marginInlineEnd: spacing.small_12,
        marginBlockEnd: spacing.xSmall_8,
    },
    rowStart: {
        alignSelf: "start",
    },
    rowEnd: {
        alignSelf: "end",
    },
    header: {
        flexDirection: "row",
        width: "100%",
        padding: spacing.small_12,
        justifyContent: "space-between",
        alignItems: "center",
    },
    headerText: {
        color: semanticColor.core.foreground.neutral.subtle,
    },
    doc: {
        borderBlockStart: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        width: "100%",
        maxHeight: 300,
        padding: spacing.small_12,
        overflowY: "auto",
    },
    button: {
        maxWidth: 200,
    },
});

const cursorStyles = StyleSheet.create({
    highlight: {
        display: "inline",
        position: "relative",
        font: "inherit",
        lineHeight: "inherit",
        margin: "inherit",
        padding: "inherit",
        color: "currentColor",
        height: "100%",
        backgroundColor: color.fadedPurple8,
        "@media print": {
            background: "none",
            backgroundColor: "transparent",
        },
    },
    cursorLine: {
        color: GuideColors.darkPurple,
        width: 0,
        outline: `1px solid ${GuideColors.darkPurple}`,
        position: "relative",
        pointerEvents: "none",
        userSelect: "none",
    },
    cursorLabel: {
        position: "absolute",
        top: -2,
        insetInlineEnd: 0,
        fontFamily: "Lato, sans-serif",
        fontSize: 11,
        lineHeight: "1.8em",
        height: "1.8em",
        fontWeight: "bold",
        pointerEvents: "none",
        userSelect: "none",
        padding: `0 ${spacing.xxxxSmall_2}px`,
        color: semanticColor.core.foreground.knockout.default,
        backgroundColor: GuideColors.darkPurple,
        transform: "translate(100%, -100%)",
        borderStartEndRadius: spacing.xxxSmall_4,
        borderEndEndRadius: spacing.xxxSmall_4,
        borderStartStartRadius: spacing.xxxSmall_4,
    },
});

const StyledMark = addStyle("mark", [
    cursorStyles.highlight,
    {
        minHeight: "1em",
    },
]);
